import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, DollarSign, Download } from 'lucide-react';
import SEO, { createEventStructuredData } from '../components/SEO';

export default function KusumotoPage() {
  const entryFees = [
    { type: "Individual", price: "$45", description: "up to 5 wrestlers" },
    { type: "Team", price: "$400", description: "6-14 wrestlers, $20 per additional wrestler" },
    { type: "Both Tournaments", price: "$725", description: "Kusumoto + Jim Root", late: "$750 if paid after Jan 1st" }
  ];

  return (
    <>
      <SEO
        title="10th Annual Kusumoto Klassic Girls Wrestling Tournament"
        description="10th Annual Kusumoto Klassic Girls Wrestling Tournament on January 15, 2027 at Prospect High School, Saratoga. Double elimination format featuring top female wrestlers. Register now!"
        keywords={[
          'Kusumoto Klassic',
          'girls wrestling tournament',
          'female wrestling',
          'Prospect High School wrestling',
          'Saratoga girls wrestling',
          'California girls wrestling',
          'January 2027 wrestling',
          'women\'s wrestling tournament',
          'Bay Area girls wrestling'
        ]}
        canonical="https://jimrootwrestling.com/kusumoto"
        structuredData={createEventStructuredData(
          '10th Annual Kusumoto Klassic Girls Wrestling Tournament',
          '2027-01-15',
          'girls'
        )}
      />
      <div className="min-h-screen bg-gradient-to-br from-pink-950 via-purple-900 to-black">
      {/* Navigation Header */}
      <header className="fixed top-0 w-full z-50 bg-black/50 backdrop-blur-md border-b border-gray-800">
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-xl md:text-2xl font-bold bg-gradient-to-r from-red-500 to-yellow-500 bg-clip-text text-transparent">
              Jim Root Memorial
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors">Home</Link>
            <Link to="/jim-root" className="text-gray-300 hover:text-white transition-colors">Jim Root</Link>
            <Link to="/kusumoto" className="text-white transition-colors">Kusumoto</Link>
            <Link to="/register" className="text-gray-300 hover:text-white transition-colors">Registration</Link>
          </div>

          <Link
            to="/register"
            className="bg-red-600 hover:bg-red-700 px-6 py-2 rounded-full font-semibold transition-colors"
          >
            Register Now
          </Link>
        </nav>
      </header>

      <div className="container mx-auto px-4 py-8 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-pink-600 text-white">Girls Wrestling</Badge>
            <h1 className="text-5xl font-bold text-white mb-2">
              10th Annual Kusumoto Klassic
            </h1>
            <p className="text-xl text-gray-300">Girls Wrestling Tournament</p>

            {/* Download Flier Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-6"
            >
              <a
                href="/fliers/kusumoto-klassic-2027.pdf"
                download
                className="inline-flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Download className="w-6 h-6" />
                Download Tournament Flier
              </a>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-purple-800/50 backdrop-blur border-purple-700">
                <CardHeader className="pb-3">
                  <Calendar className="w-8 h-8 text-pink-400 mb-2" />
                  <CardTitle className="text-white">Date</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">Friday</p>
                  <p className="text-2xl font-bold text-pink-400">January 15, 2027</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-purple-800/50 backdrop-blur border-purple-700">
                <CardHeader className="pb-3">
                  <MapPin className="w-8 h-8 text-pink-400 mb-2" />
                  <CardTitle className="text-white">Location</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">Prospect High School</p>
                  <p className="text-sm text-gray-400">18900 Prospect Rd, Saratoga, CA 95070</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Registration Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <Card className="bg-purple-800/50 backdrop-blur border-purple-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="w-6 h-6 text-pink-400" />
                  Registration Fees
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {entryFees.map((fee, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="bg-purple-700/50 p-4 rounded-lg border border-purple-600"
                    >
                      <p className="text-white font-semibold text-sm">{fee.type}</p>
                      <p className="text-2xl font-bold text-pink-400 my-1">{fee.price}</p>
                      <p className="text-gray-300 text-xs">{fee.description}</p>
                      {fee.late && (
                        <p className="text-orange-400 text-xs mt-1">{fee.late}</p>
                      )}
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <p className="text-center text-sm text-gray-400 mb-8">
            Schedule, weigh-ins, weight classes, awards and seeding are all on the
            flier &mdash;{' '}
            <a href="/fliers/kusumoto-klassic-2027.pdf" download className="text-pink-400 hover:text-pink-300 underline">
              download it here
            </a>.
          </p>

        </motion.div>
      </div>
    </div>
    </>
  );
}
